import React, { useState, useEffect, useRef, useMemo } from "react";
import { parse, evaluate } from "mathjs";

/* =========================================================================
   Physics Plotter — in-house, MIT/free-licensed alternative to Desmos
   A comprehensive physics plotting tool.
   - Parses any y=f(x) expression with mathjs
   - Auto-detects free parameters and creates sliders for them
   - Renders to an HTML5 Canvas (no external plot library)
   - Pre-loaded physics templates (projectile, SHM, RC, wave, etc.)
   - Two simultaneous curves for comparison
   - PNG export
   - URL permalink for sharing a configured plot
   ========================================================================= */

const RESERVED = new Set([
  "x", "pi", "e", "true", "false", "Infinity", "NaN",
  "sin", "cos", "tan", "asin", "acos", "atan", "atan2",
  "sinh", "cosh", "tanh", "exp", "log", "log10", "log2", "ln",
  "sqrt", "cbrt", "abs", "ceil", "floor", "round", "sign",
  "min", "max", "pow", "mod",
]);

const TEMPLATES = [
  /* ── Kinematics ── */
  {
    group: "Kinematics",
    name: "🪂 Free fall — distance vs time",
    note: "s = ½ g t² · x is time.",
    expr: "(1/2)*g*x^2",
    defaults: { g: 9.81 },
    xRange: [0, 5], yRange: [0, 130],
    questions: [
      { id: "ff1", level: "easy", type: "numeric", unit: "m", tolerance: 1,
        prompt: "With g = 9.81 m/s², an object dropped from rest falls for 3 s. From the plot, what is the distance fallen?",
        answer: 44.145,
        hint: "Read the y-value of the curve at x = 3 s. Equation: s = ½ g t².",
        solution: "s = ½ × 9.81 × 3² = 44.145 m. The s–t curve is a parabola opening upward." },
      { id: "ff2", level: "easy", type: "mcq",
        prompt: "What is the shape of the distance–time curve for free fall from rest?",
        options: ["Straight line", "Parabola opening upward", "Exponential decay", "Hyperbola"],
        correct: 1,
        solution: "s = ½ g t² — a quadratic in t, plotting as an upward parabola starting at the origin." },
      { id: "ff3", level: "medium", type: "numeric", unit: "s", tolerance: 0.1,
        prompt: "Use the plot to find the time at which the object has fallen 50 m.",
        answer: 3.193,
        hint: "Set ½ g t² = 50 and solve for t. Or read x where the curve crosses y = 50.",
        solution: "t = √(2·50/9.81) = √(10.194) ≈ 3.19 s." },
      { id: "ff4", level: "medium", type: "numeric", unit: "factor", tolerance: 0.05,
        prompt: "If g is halved (set the slider to g ≈ 4.91), by what factor does the distance fallen in any fixed time change compared with g = 9.81?",
        answer: 0.5,
        hint: "s ∝ g for fixed t. Drag the slider and compare.",
        solution: "Since s = ½ g t², halving g halves the distance for the same time. Factor = 0.5." },
      { id: "ff5", level: "jee-mains", type: "numeric", unit: "s", tolerance: 0.2,
        prompt: "Set g = 1.62 m/s² (Moon gravity). How long does an object take to fall 100 m from rest?",
        answer: 11.111,
        hint: "Use s = ½ g t², plot it, find x where y = 100.",
        solution: "t = √(2·100/1.62) ≈ √123.46 ≈ 11.11 s." },
    ],
  },
  {
    group: "Kinematics",
    name: "📏 Uniformly accelerated — position",
    note: "s = u t + ½ a t² · x is time.",
    expr: "u*x + (1/2)*a*x^2",
    defaults: { u: 5, a: 2 },
    xRange: [0, 10], yRange: [0, 150],
    questions: [
      { id: "ua1", level: "easy", type: "numeric", unit: "m", tolerance: 0.5,
        prompt: "With u = 5 m/s and a = 2 m/s², what is the position at t = 4 s?",
        answer: 36,
        solution: "s = u·t + ½·a·t² = 5·4 + ½·2·16 = 20 + 16 = 36 m." },
      { id: "ua2", level: "easy", type: "mcq",
        prompt: "The slope of a v–t graph gives:",
        options: ["Distance", "Velocity", "Acceleration", "Jerk"],
        correct: 2,
        solution: "Acceleration is dv/dt = slope of velocity vs time." },
      { id: "ua3", level: "medium", type: "numeric", unit: "s", tolerance: 0.2,
        prompt: "Using the plot with u=5, a=2: at what time does the body reach 50 m?",
        answer: 5.658,
        hint: "Solve 5t + t² = 50 for t > 0.",
        solution: "t² + 5t − 50 = 0 → t = (−5 + √(25 + 200))/2 ≈ 5.66 s." },
      { id: "ua4", level: "jee-mains", type: "numeric", unit: "m/s", tolerance: 0.5,
        prompt: "Starting from rest (u = 0) with constant acceleration a = 3 m/s², what is the speed at t = 6 s?",
        answer: 18,
        solution: "v = u + a·t = 0 + 3·6 = 18 m/s." },
    ],
  },
  {
    group: "Kinematics",
    name: "🏃 Velocity vs time",
    note: "v = u + a t · x is time.",
    expr: "u + a*x",
    defaults: { u: 0, a: 9.81 },
    xRange: [0, 10], yRange: [0, 100],
    questions: [
      { id: "vt1", level: "eamcet", type: "mcq",
        prompt: "On a velocity–time graph, the area under the curve between two times represents:",
        options: ["Acceleration", "Displacement", "Total distance always", "Average velocity"],
        correct: 1,
        solution: "Area under v–t = ∫v dt = displacement. (It equals distance only when motion is in one direction.)" },
      { id: "vt2", level: "medium", type: "numeric", unit: "m/s²", tolerance: 0.1,
        prompt: "A particle's velocity changes linearly from 4 m/s to 16 m/s over 6 s. From the v–t plot, what is the acceleration?",
        answer: 2,
        solution: "Slope = (16 − 4)/6 = 12/6 = 2 m/s²." },
      { id: "vt3", level: "jee-mains", type: "numeric", unit: "m", tolerance: 1,
        prompt: "Trick: A body moves with constant velocity 5 m/s for 4 s, then decelerates uniformly to rest in the next 6 s. Total distance covered?",
        answer: 35,
        hint: "Distance = area under v–t = rectangle + triangle.",
        solution: "Rectangle: 5·4 = 20 m. Triangle: ½·6·5 = 15 m. Total = 35 m." },
      { id: "vt4", level: "jee-adv", type: "mcq",
        prompt: "If the v–t graph is a straight line passing through the origin with positive slope, the motion is:",
        options: [
          "Uniform velocity",
          "Uniform acceleration from rest",
          "Uniformly decelerated",
          "Non-uniform acceleration",
        ],
        correct: 1,
        solution: "v ∝ t with v = 0 at t = 0 implies v = a·t, constant a, starting from rest." },
    ],
  },
  {
    group: "Kinematics",
    name: "🚀 Projectile — height vs horizontal distance",
    note: "θ in radians.",
    expr: "x*tan(theta) - (g/(2*u^2*cos(theta)^2)) * x^2",
    defaults: { theta: 0.785, u: 20, g: 9.81 },
    xRange: [0, 40], yRange: [0, 15],
    questions: [
      { id: "pr1", level: "easy", type: "numeric", unit: "m", tolerance: 0.5,
        prompt: "With u = 20 m/s, θ = π/4 (45°), g = 9.81 m/s²: read the maximum height from the plot.",
        answer: 10.194,
        hint: "Max height = u²·sin²θ / (2g).",
        solution: "H = (20² × sin²45°) / (2 × 9.81) = (400 × 0.5) / 19.62 ≈ 10.2 m." },
      { id: "pr2", level: "easy", type: "numeric", unit: "m", tolerance: 1,
        prompt: "With the same parameters, what is the horizontal range (where the curve hits y = 0 again)?",
        answer: 40.78,
        solution: "R = u²·sin(2θ)/g = (400·sin90°)/9.81 ≈ 40.78 m." },
      { id: "pr3", level: "medium", type: "mcq",
        prompt: "Two projectiles are fired with the same speed at 30° and 60°. Their ranges are:",
        options: ["Range at 60° is greater", "Range at 30° is greater", "Both ranges are equal", "Range depends on mass"],
        correct: 2,
        solution: "Complementary angles 30° and 60° give the same range because sin(60°) = sin(120°). Hence R₃₀ = R₆₀." },
      { id: "pr4", level: "jee-mains", type: "numeric", unit: "degrees", tolerance: 1,
        prompt: "For a given speed, at what angle (in degrees) is the projectile's range maximum?",
        answer: 45,
        solution: "R = u²·sin(2θ)/g is maximum when sin(2θ) = 1, i.e. 2θ = 90° → θ = 45°." },
    ],
  },

  /* ── Dynamics & circular motion ── */
  {
    group: "Dynamics",
    name: "🎯 Centripetal force vs radius",
    note: "F = m v² / r · x is radius.",
    expr: "m*v^2 / x",
    defaults: { m: 1, v: 5 },
    xRange: [0.2, 10], yRange: [0, 80],
    questions: [
      { id: "cf1", level: "easy", type: "numeric", unit: "N", tolerance: 0.5,
        prompt: "A 1 kg ball moves in a circle of radius 5 m at 5 m/s. Find the centripetal force.",
        answer: 5,
        solution: "F = m v² / r = 1 × 25 / 5 = 5 N." },
      { id: "cf2", level: "eamcet", type: "mcq",
        prompt: "If the speed of a body in uniform circular motion is doubled while the radius stays the same, the centripetal force becomes:",
        options: ["Half", "Same", "Doubled", "Four times"],
        correct: 3,
        solution: "F ∝ v². Doubling v multiplies F by 4." },
      { id: "cf3", level: "medium", type: "numeric", unit: "N", tolerance: 0.5,
        prompt: "Using m = 2 kg, v = 10 m/s, what is the centripetal force needed at r = 4 m? Verify from the plot.",
        answer: 50,
        solution: "F = 2 × 100 / 4 = 50 N." },
      { id: "cf4", level: "jee-mains", type: "numeric", unit: "factor", tolerance: 0.05,
        prompt: "If the radius is tripled while keeping the speed constant, the centripetal force changes by a factor of:",
        answer: 0.3333,
        solution: "F = mv²/r → F ∝ 1/r. Tripling r gives F → F/3 ≈ 0.333." },
    ],
  },
  {
    group: "Dynamics",
    name: "🧱 Friction force vs normal force",
    note: "F = μ N · x is normal force.",
    expr: "mu*x",
    defaults: { mu: 0.3 },
    xRange: [0, 100], yRange: [0, 40],
  },

  /* ── Work, Energy, Power ── */
  {
    group: "Work & Energy",
    name: "⚡ Kinetic energy vs speed",
    note: "KE = ½ m v² · x is speed.",
    expr: "(1/2)*m*x^2",
    defaults: { m: 1 },
    xRange: [0, 20], yRange: [0, 220],
    questions: [
      { id: "ke1", level: "easy", type: "numeric", unit: "J", tolerance: 0.5,
        prompt: "Find the kinetic energy of a 1 kg body moving at 10 m/s.",
        answer: 50,
        solution: "KE = ½·1·10² = 50 J." },
      { id: "ke2", level: "eamcet", type: "mcq",
        prompt: "If the speed of a body becomes three times its initial value, its kinetic energy becomes:",
        options: ["Three times", "Six times", "Nine times", "Twelve times"],
        correct: 2,
        solution: "KE ∝ v². v → 3v ⇒ KE → 9·KE." },
      { id: "ke3", level: "medium", type: "numeric", unit: "m/s", tolerance: 0.2,
        prompt: "A 2 kg body has KE = 36 J. Find its speed. (Use m = 2 in the plotter.)",
        answer: 6,
        solution: "v = √(2·KE/m) = √(2·36/2) = √36 = 6 m/s." },
      { id: "ke4", level: "jee-mains", type: "numeric", unit: "%", tolerance: 1,
        prompt: "By what percentage does the kinetic energy of an object increase when its speed increases by 20%?",
        answer: 44,
        hint: "KE ratio = (1.2)² = 1.44.",
        solution: "KE_final/KE_initial = (v_final/v_initial)² = 1.2² = 1.44. Increase = 44%." },
    ],
  },
  {
    group: "Work & Energy",
    name: "🌀 Spring potential energy",
    note: "U = ½ k x² · x is displacement.",
    expr: "(1/2)*k*x^2",
    defaults: { k: 100 },
    xRange: [-0.5, 0.5], yRange: [0, 15],
    questions: [
      { id: "sp1", level: "easy", type: "numeric", unit: "J", tolerance: 0.1,
        prompt: "A spring with k = 100 N/m is compressed by 0.2 m. What is the stored potential energy?",
        answer: 2,
        solution: "U = ½·100·(0.2)² = ½·100·0.04 = 2 J." },
      { id: "sp2", level: "eamcet", type: "mcq",
        prompt: "If the compression of a spring is doubled, the potential energy stored becomes:",
        options: ["Same", "Doubled", "Four times", "Half"],
        correct: 2,
        solution: "U ∝ x². Doubling x makes U four times larger." },
      { id: "sp3", level: "medium", type: "numeric", unit: "N/m", tolerance: 1,
        prompt: "A spring is stretched by 0.1 m and stores 0.5 J of energy. Find its spring constant.",
        answer: 100,
        solution: "k = 2U/x² = 2·0.5/0.01 = 100 N/m." },
      { id: "sp4", level: "jee-mains", type: "numeric", unit: "m/s", tolerance: 0.1,
        prompt: "A 1 kg block released from compression 0.2 m of a spring with k = 100 N/m loses contact at natural length on a frictionless surface. Find its speed at that instant.",
        answer: 2,
        hint: "Energy conservation: ½·k·x² = ½·m·v².",
        solution: "½·100·(0.2)² = ½·1·v² ⇒ v² = 4 ⇒ v = 2 m/s." },
    ],
  },
  {
    group: "Work & Energy",
    name: "🏔️ Gravitational PE near surface",
    note: "U = m g h · x is height.",
    expr: "m*g*x",
    defaults: { m: 1, g: 9.81 },
    xRange: [0, 50], yRange: [0, 500],
  },

  /* ── Gravitation ── */
  {
    group: "Gravitation",
    name: "🌍 g vs altitude",
    note: "g(h) = g₀·(R/(R+h))² · x is altitude (km).",
    expr: "g0 * (R/(R+x))^2",
    defaults: { g0: 9.81, R: 6371 },
    xRange: [0, 30000], yRange: [0, 10],
    questions: [
      { id: "gh1", level: "easy", type: "mcq",
        prompt: "As altitude above Earth's surface increases, the acceleration due to gravity:",
        options: ["Increases linearly", "Stays constant", "Decreases", "Becomes zero immediately"],
        correct: 2,
        solution: "g(h) = g₀·(R/(R+h))² — strictly decreasing in h." },
      { id: "gh2", level: "eamcet", type: "numeric", unit: "m/s²", tolerance: 0.05,
        prompt: "At an altitude equal to Earth's radius R, what is the value of g? (Use g₀ = 9.81 m/s².)",
        answer: 2.4525,
        solution: "g(R) = 9.81·(R/(2R))² = 9.81/4 ≈ 2.45 m/s²." },
      { id: "gh3", level: "jee-mains", type: "numeric", unit: "%", tolerance: 1,
        prompt: "By what approximate percentage does g decrease at an altitude of 100 km above Earth's surface? (R = 6371 km, g₀ = 9.81 m/s².)",
        answer: 3.1,
        hint: "Compute g(100)/g₀ and find percentage change.",
        solution: "g(100)/g₀ = (6371/6471)² ≈ 0.9693 → decrease ≈ 3.1%." },
    ],
  },
  {
    group: "Gravitation",
    name: "🛰️ Gravitational force vs distance",
    note: "F = G M m / r² · x is r.",
    expr: "G*M*m / (x^2)",
    defaults: { G: 6.674e-11, M: 5.972e24, m: 1 },
    xRange: [6.4e6, 5e7], yRange: [0, 12],
  },

  /* ── Oscillations ── */
  {
    group: "Oscillations",
    name: "🌀 Simple Harmonic Motion — displacement",
    note: "x(t) = A sin(ω t + φ) · x is time.",
    expr: "A*sin(omega*x + phi)",
    defaults: { A: 1, omega: 2, phi: 0 },
    xRange: [0, 10], yRange: [-2, 2],
    questions: [
      { id: "shm1", level: "easy", type: "numeric", unit: "s", tolerance: 0.05,
        prompt: "With ω = 2 rad/s, what is the period T of the motion?",
        answer: 3.1416,
        solution: "T = 2π/ω = 2π/2 = π ≈ 3.14 s." },
      { id: "shm2", level: "easy", type: "numeric", unit: "m/s", tolerance: 0.05,
        prompt: "With A = 1 m, ω = 2 rad/s, what is the maximum speed of the particle?",
        answer: 2,
        solution: "v_max = A·ω = 1·2 = 2 m/s." },
      { id: "shm3", level: "medium", type: "mcq",
        prompt: "At the extreme position of an SHM, the magnitude of acceleration is:",
        options: ["Zero", "Maximum, equal to A·ω²", "Equal to A·ω", "Equal to A/ω"],
        correct: 1,
        solution: "a = −ω²·x. At x = ±A the magnitude is maximum: a_max = A·ω²." },
      { id: "shm4", level: "jee-mains", type: "numeric", unit: "ratio", tolerance: 0.02,
        prompt: "For an SHM, what fraction of the total energy is kinetic when displacement is half the amplitude (x = A/2)?",
        answer: 0.75,
        hint: "Total E = ½ m ω² A². At x = A/2, U = ½ m ω² (A/2)² = E/4.",
        solution: "U/E = (x/A)² = 1/4 → KE/E = 1 − 1/4 = 3/4 = 0.75." },
    ],
  },
  {
    group: "Oscillations",
    name: "🏃‍♂️ SHM velocity",
    note: "v(t) = A ω cos(ω t).",
    expr: "A*omega*cos(omega*x)",
    defaults: { A: 1, omega: 2 },
    xRange: [0, 10], yRange: [-3, 3],
  },
  {
    group: "Oscillations",
    name: "📉 Damped Oscillator",
    note: "x(t) = A e^(−b t) cos(ω t).",
    expr: "A*exp(-b*x)*cos(omega*x)",
    defaults: { A: 1, b: 0.2, omega: 4 },
    xRange: [0, 20], yRange: [-1.2, 1.2],
  },
  {
    group: "Oscillations",
    name: "🕰️ Pendulum period vs length",
    note: "T = 2π √(L/g) · x is length.",
    expr: "2*pi*sqrt(x/g)",
    defaults: { g: 9.81 },
    xRange: [0.1, 5], yRange: [0, 5],
    questions: [
      { id: "pd1", level: "easy", type: "numeric", unit: "s", tolerance: 0.05,
        prompt: "From the plot with g = 9.81 m/s², find the period of a pendulum of length 1 m.",
        answer: 2.006,
        solution: "T = 2π√(1/9.81) ≈ 2.006 s." },
      { id: "pd2", level: "medium", type: "numeric", unit: "factor", tolerance: 0.05,
        prompt: "If the length of a pendulum is doubled, by what factor does its period change?",
        answer: 1.414,
        solution: "T ∝ √L. Doubling L gives factor √2 ≈ 1.414." },
      { id: "pd3", level: "jee-mains", type: "numeric", unit: "factor", tolerance: 0.05,
        prompt: "Slide g down to 1.62 m/s² (Moon). For the same length 1 m, by what factor is the Moon period larger than the Earth period?",
        answer: 2.461,
        hint: "T_Moon/T_Earth = √(g_Earth / g_Moon).",
        solution: "Ratio = √(9.81/1.62) = √6.06 ≈ 2.46." },
    ],
  },

  /* ── Waves ── */
  {
    group: "Waves",
    name: "🌊 Travelling wave (snapshot t = 0)",
    note: "y = A sin(k x).",
    expr: "A*sin(k*x)",
    defaults: { A: 1, k: 1 },
    xRange: [0, 20], yRange: [-2, 2],
  },
  {
    group: "Waves",
    name: "🎵 Beats (superposition of two close-frequency waves)",
    note: "y = sin(ω₁ t) + sin(ω₂ t).",
    expr: "sin(omega1*x) + sin(omega2*x)",
    defaults: { omega1: 10, omega2: 11 },
    xRange: [0, 8], yRange: [-2.5, 2.5],
  },
  {
    group: "Waves",
    name: "📡 Doppler shift (approaching observer)",
    note: "f' = f (v + v_obs) / (v − v_src) · x is v_src.",
    expr: "f*(v + v_obs) / (v - x)",
    defaults: { f: 500, v: 340, v_obs: 0 },
    xRange: [0, 300], yRange: [500, 2500],
  },

  /* ── Properties of matter / fluids ── */
  {
    group: "Properties of Matter",
    name: "📏 Stress–strain (linear region)",
    note: "σ = Y ε · x is strain.",
    expr: "Y*x",
    defaults: { Y: 2e11 },
    xRange: [0, 0.002], yRange: [0, 4e8],
  },
  {
    group: "Properties of Matter",
    name: "🌊 Pressure with depth",
    note: "P = P₀ + ρ g h · x is depth.",
    expr: "P0 + rho*g*x",
    defaults: { P0: 1.01e5, rho: 1000, g: 9.81 },
    xRange: [0, 100], yRange: [0, 1.5e6],
  },

  /* ── Thermal physics ── */
  {
    group: "Thermal",
    name: "🌡️ Ideal gas — P vs V (isotherm)",
    note: "P V = n R T · x is V.",
    expr: "n*R*T / x",
    defaults: { n: 1, R: 8.314, T: 300 },
    xRange: [0.001, 0.05], yRange: [0, 2.5e6],
  },
  {
    group: "Thermal",
    name: "🔥 Stefan radiation — Power vs Temperature",
    note: "P = σ A T⁴ · x is T (K).",
    expr: "sigma*A*x^4",
    defaults: { sigma: 5.67e-8, A: 1 },
    xRange: [100, 2000], yRange: [0, 1e6],
  },
  {
    group: "Thermal",
    name: "🌡️ Maxwell–Boltzmann speed distribution (shape)",
    note: "Approximate shape; β bundles m, T, k_B.",
    expr: "4*pi*x^2 * (beta/pi)^1.5 * exp(-beta*x^2)",
    defaults: { beta: 0.01 },
    xRange: [0, 50], yRange: [0, 0.04],
  },

  /* ── Electricity, magnetism, optics (XII + bonus) ── */
  {
    group: "Electromagnetism",
    name: "🔋 RC charging — V vs time",
    note: "V(t) = V₀ (1 − e^(−t/RC)).",
    expr: "V0*(1 - exp(-x/(R*C)))",
    defaults: { V0: 5, R: 1, C: 1 },
    xRange: [0, 8], yRange: [0, 6],
    questions: [
      { id: "rc1", level: "easy", type: "mcq",
        prompt: "As a capacitor charges through a resistor, the voltage across it:",
        options: ["Stays constant", "Rises linearly", "Rises and approaches V₀ exponentially", "Oscillates"],
        correct: 2,
        solution: "V(t) = V₀·(1 − e^(−t/RC)) → exponential approach to V₀." },
      { id: "rc2", level: "eamcet", type: "numeric", unit: "fraction of V₀", tolerance: 0.02,
        prompt: "At time t = RC (one time constant), the capacitor is charged to what fraction of V₀?",
        answer: 0.632,
        solution: "V(RC)/V₀ = 1 − e^(−1) ≈ 0.632." },
      { id: "rc3", level: "medium", type: "numeric", unit: "s", tolerance: 0.05,
        prompt: "With R = 1 Ω and C = 1 F, after how many time constants is the voltage 95% of V₀?",
        answer: 3,
        hint: "Solve 1 − e^(−t) = 0.95 for t (in units of RC).",
        solution: "e^(−t) = 0.05 → t = ln(20) ≈ 3.0 time constants." },
      { id: "rc4", level: "jee-mains", type: "numeric", unit: "s", tolerance: 0.1,
        prompt: "An RC circuit has R = 2 Ω and C = 0.5 F. Time constant τ?",
        answer: 1,
        solution: "τ = R·C = 2·0.5 = 1 s." },
    ],
  },
  {
    group: "Electromagnetism",
    name: "🧲 Coulomb force vs separation",
    note: "F = k q₁ q₂ / r².",
    expr: "k*q1*q2 / (x^2)",
    defaults: { k: 8.99, q1: 1, q2: 1 },
    xRange: [0.5, 10], yRange: [0, 40],
    questions: [
      { id: "co1", level: "easy", type: "mcq",
        prompt: "Coulomb's force between two point charges varies with separation r as:",
        options: ["1/r", "1/r²", "1/r³", "r²"],
        correct: 1,
        solution: "F = k q₁ q₂ / r² — inverse-square law." },
      { id: "co2", level: "eamcet", type: "mcq",
        prompt: "If the distance between two charges is doubled, the magnitude of the force between them becomes:",
        options: ["Half", "One-fourth", "Doubled", "Same"],
        correct: 1,
        solution: "F ∝ 1/r². r → 2r ⇒ F → F/4." },
      { id: "co3", level: "medium", type: "numeric", unit: "N", tolerance: 0.5,
        prompt: "Two charges of 1 C each are separated by 3 m. Using k = 8.99 (in units where r is in meters and the answer is in 10⁹ N), find the force.",
        answer: 0.999,
        hint: "F = k·q₁·q₂/r² = 8.99/9.",
        solution: "F = 8.99·1·1/9 ≈ 0.999 × 10⁹ N (using k in standard form)." },
      { id: "co4", level: "jee-mains", type: "numeric", unit: "factor", tolerance: 0.05,
        prompt: "If both charges are tripled and the distance is also tripled, the new force compared to the original is by a factor of:",
        answer: 1,
        hint: "F' = k(3q₁)(3q₂)/(3r)² = 9·k·q₁·q₂/(9·r²).",
        solution: "Numerator multiplies by 9, denominator multiplies by 9 ⇒ unchanged. Factor = 1." },
    ],
  },
  {
    group: "Optics",
    name: "💡 Thin lens — image distance vs object distance",
    note: "1/v = 1/f − 1/u · x is u.",
    expr: "1/(1/f - 1/x)",
    defaults: { f: 10 },
    xRange: [15, 50], yRange: [0, 60],
    questions: [
      { id: "ln1", level: "easy", type: "numeric", unit: "cm", tolerance: 0.5,
        prompt: "A converging lens has f = 10 cm. For an object at u = 30 cm, find the image distance v.",
        answer: 15,
        solution: "1/v = 1/10 − 1/30 = (3−1)/30 = 2/30 → v = 15 cm." },
      { id: "ln2", level: "eamcet", type: "mcq",
        prompt: "For a converging lens, an object placed at distance equal to 2f produces an image:",
        options: ["At infinity", "At 2f, real and inverted", "At f, virtual", "At f, real"],
        correct: 1,
        solution: "u = 2f → v = 2f, magnification = 1 (real and inverted, same size)." },
      { id: "ln3", level: "jee-mains", type: "numeric", unit: "cm", tolerance: 0.5,
        prompt: "An object is at u = 20 cm from a lens of focal length f = 15 cm. Find the image distance v.",
        answer: 60,
        solution: "1/v = 1/15 − 1/20 = (4−3)/60 = 1/60 → v = 60 cm." },
    ],
  },
];

/* Parse an expression and pull out parameter names (symbols not in RESERVED). */
function detectParams(expr) {
  try {
    const tree = parse(expr);
    const found = new Set();
    tree.traverse((node) => {
      if (node.isSymbolNode && !RESERVED.has(node.name)) {
        found.add(node.name);
      }
    });
    return Array.from(found);
  } catch {
    return [];
  }
}

export default function PhysicsPlotterPage() {
  // initial state from URL if present — start with the first template that has questions
  const initial = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    const seed = TEMPLATES.find(t => t.questions?.length) || TEMPLATES[0];
    return {
      expr1: p.get("f1") || seed.expr,
      expr2: p.get("f2") || "",
      params: (() => {
        const obj = {};
        for (const [k, v] of p.entries()) {
          if (k.startsWith("p_")) obj[k.slice(2)] = parseFloat(v);
        }
        return Object.keys(obj).length ? obj : { ...seed.defaults };
      })(),
      xMin: parseFloat(p.get("xmin")) || seed.xRange[0],
      xMax: parseFloat(p.get("xmax")) || seed.xRange[1],
      yMin: parseFloat(p.get("ymin")) || seed.yRange[0],
      yMax: parseFloat(p.get("ymax")) || seed.yRange[1],
    };
  }, []);

  const [expr1, setExpr1] = useState(initial.expr1);
  const [expr2, setExpr2] = useState(initial.expr2);
  const [params, setParams] = useState(initial.params);
  const [xMin, setXMin] = useState(initial.xMin);
  const [xMax, setXMax] = useState(initial.xMax);
  const [yMin, setYMin] = useState(initial.yMin);
  const [yMax, setYMax] = useState(initial.yMax);
  const [error, setError] = useState("");
  const canvasRef = useRef(null);

  // Practice state — default to the first template that has questions so the panel is visible on first load
  const [currentTemplate, setCurrentTemplate] = useState(() => TEMPLATES.find(t => t.questions?.length) || TEMPLATES[0]);
  const [qIdx, setQIdx] = useState(0);                          // current question index
  const [userInput, setUserInput] = useState("");               // raw input string
  const [mcqChoice, setMcqChoice] = useState(null);             // selected mcq option index
  const [feedback, setFeedback] = useState(null);               // "correct" | "wrong" | null
  const [showHint, setShowHint] = useState(false);
  const [showSolution, setShowSolution] = useState(false);
  const [completed, setCompleted] = useState({});               // { [templateName]: Set([qIds]) }

  const usedParams = useMemo(() => {
    const a = detectParams(expr1);
    const b = expr2 ? detectParams(expr2) : [];
    return Array.from(new Set([...a, ...b])).sort();
  }, [expr1, expr2]);

  // Whenever the detected params change, make sure each has a value
  useEffect(() => {
    setParams((prev) => {
      const next = { ...prev };
      usedParams.forEach((k) => { if (next[k] === undefined) next[k] = 1; });
      return next;
    });
  }, [usedParams]);

  // Redraw whenever inputs change
  useEffect(() => {
    drawPlot();
  }, [expr1, expr2, params, xMin, xMax, yMin, yMax]);

  function applyTemplate(t) {
    setExpr1(t.expr);
    setExpr2("");
    setParams({ ...t.defaults });
    setXMin(t.xRange[0]); setXMax(t.xRange[1]);
    setYMin(t.yRange[0]); setYMax(t.yRange[1]);
    setCurrentTemplate(t);
    setQIdx(0); setUserInput(""); setMcqChoice(null);
    setFeedback(null); setShowHint(false); setShowSolution(false);
  }

  // Load completed-question progress from localStorage on mount
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("rgukt-plotter-progress") || "{}");
      const restored = {};
      Object.entries(saved).forEach(([k, arr]) => { restored[k] = new Set(arr); });
      setCompleted(restored);
    } catch {}
  }, []);

  function persistCompleted(next) {
    const obj = {};
    Object.entries(next).forEach(([k, set]) => { obj[k] = Array.from(set); });
    try { localStorage.setItem("rgukt-plotter-progress", JSON.stringify(obj)); } catch {}
  }

  function checkAnswer() {
    if (!currentTemplate) return;
    const q = currentTemplate.questions?.[qIdx];
    if (!q) return;
    let ok = false;
    if (q.type === "numeric") {
      const val = parseFloat(userInput);
      if (Number.isFinite(val) && Math.abs(val - q.answer) <= (q.tolerance ?? 0.5)) ok = true;
    } else if (q.type === "mcq") {
      if (mcqChoice === q.correct) ok = true;
    }
    setFeedback(ok ? "correct" : "wrong");
    if (ok) {
      const key = currentTemplate.name;
      const set = new Set(completed[key] || []);
      set.add(q.id);
      const next = { ...completed, [key]: set };
      setCompleted(next);
      persistCompleted(next);
    }
  }

  function nextQuestion(delta) {
    if (!currentTemplate?.questions) return;
    const n = currentTemplate.questions.length;
    setQIdx((i) => Math.max(0, Math.min(n - 1, i + delta)));
    setUserInput(""); setMcqChoice(null);
    setFeedback(null); setShowHint(false); setShowSolution(false);
  }

  function evaluateAt(expr, x) {
    if (!expr) return null;
    try {
      const scope = { x, ...params, pi: Math.PI, e: Math.E };
      const val = evaluate(expr, scope);
      return typeof val === "number" && isFinite(val) ? val : null;
    } catch {
      return null;
    }
  }

  function drawPlot() {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const dpr = window.devicePixelRatio || 1;
    const W = cvs.clientWidth * dpr;
    const H = cvs.clientHeight * dpr;
    cvs.width = W; cvs.height = H;
    const ctx = cvs.getContext("2d");
    ctx.clearRect(0, 0, W, H);

    // Backgrounds + axes
    const PAD = 50 * dpr;
    const plotW = W - PAD * 2;
    const plotH = H - PAD * 2;
    const xToPx = (xVal) => PAD + ((xVal - xMin) / (xMax - xMin)) * plotW;
    const yToPx = (yVal) => PAD + plotH - ((yVal - yMin) / (yMax - yMin)) * plotH;

    // Grid
    ctx.strokeStyle = "#e0e4f3";
    ctx.lineWidth = 1 * dpr;
    const xSteps = 10, ySteps = 8;
    for (let i = 0; i <= xSteps; i++) {
      const x = xMin + (i / xSteps) * (xMax - xMin);
      const px = xToPx(x);
      ctx.beginPath(); ctx.moveTo(px, PAD); ctx.lineTo(px, PAD + plotH); ctx.stroke();
    }
    for (let i = 0; i <= ySteps; i++) {
      const y = yMin + (i / ySteps) * (yMax - yMin);
      const py = yToPx(y);
      ctx.beginPath(); ctx.moveTo(PAD, py); ctx.lineTo(PAD + plotW, py); ctx.stroke();
    }

    // Axes
    ctx.strokeStyle = "#37474f";
    ctx.lineWidth = 1.5 * dpr;
    ctx.font = `${12 * dpr}px Segoe UI, sans-serif`;
    ctx.fillStyle = "#37474f";
    // x=0 vertical axis (if visible)
    if (xMin < 0 && xMax > 0) {
      ctx.beginPath(); ctx.moveTo(xToPx(0), PAD); ctx.lineTo(xToPx(0), PAD + plotH); ctx.stroke();
    }
    // y=0 horizontal axis (if visible)
    if (yMin < 0 && yMax > 0) {
      ctx.beginPath(); ctx.moveTo(PAD, yToPx(0)); ctx.lineTo(PAD + plotW, yToPx(0)); ctx.stroke();
    }
    // Plot boundary box
    ctx.strokeStyle = "#1a237e";
    ctx.lineWidth = 1.5 * dpr;
    ctx.strokeRect(PAD, PAD, plotW, plotH);
    // x ticks + labels
    ctx.textAlign = "center"; ctx.textBaseline = "top";
    for (let i = 0; i <= xSteps; i++) {
      const x = xMin + (i / xSteps) * (xMax - xMin);
      ctx.fillText(fmt(x), xToPx(x), PAD + plotH + 6 * dpr);
    }
    // y ticks + labels
    ctx.textAlign = "right"; ctx.textBaseline = "middle";
    for (let i = 0; i <= ySteps; i++) {
      const y = yMin + (i / ySteps) * (yMax - yMin);
      ctx.fillText(fmt(y), PAD - 6 * dpr, yToPx(y));
    }
    // Axis titles
    ctx.textAlign = "center"; ctx.textBaseline = "alphabetic";
    ctx.font = `bold ${13 * dpr}px Segoe UI, sans-serif`;
    ctx.fillText("x", PAD + plotW / 2, H - 12 * dpr);
    ctx.save(); ctx.translate(18 * dpr, PAD + plotH / 2); ctx.rotate(-Math.PI / 2);
    ctx.fillText("y", 0, 0); ctx.restore();

    // Curves
    const COLOURS = ["#1565c0", "#e65100"];
    [expr1, expr2].forEach((expr, ci) => {
      if (!expr) return;
      let err = null;
      ctx.strokeStyle = COLOURS[ci];
      ctx.lineWidth = 2.5 * dpr;
      ctx.beginPath();
      let drawing = false;
      const N = Math.floor(plotW);
      for (let i = 0; i <= N; i++) {
        const xVal = xMin + (i / N) * (xMax - xMin);
        const yVal = evaluateAt(expr, xVal);
        if (yVal == null) { drawing = false; continue; }
        if (yVal < yMin - 1e6 || yVal > yMax + 1e6) { drawing = false; continue; }
        const px = xToPx(xVal);
        const py = yToPx(yVal);
        if (!drawing) { ctx.moveTo(px, py); drawing = true; }
        else ctx.lineTo(px, py);
      }
      ctx.stroke();
      // Legend dot
      const lx = PAD + 10 * dpr, ly = PAD + 14 * dpr + ci * 18 * dpr;
      ctx.fillStyle = COLOURS[ci];
      ctx.fillRect(lx, ly - 4 * dpr, 14 * dpr, 8 * dpr);
      ctx.fillStyle = "#1a237e"; ctx.textAlign = "left"; ctx.textBaseline = "middle";
      ctx.font = `${12 * dpr}px Segoe UI, sans-serif`;
      ctx.fillText("y" + (ci + 1) + " = " + expr, lx + 20 * dpr, ly);
    });

    // Detect errors in expressions
    try { parse(expr1); setError(""); }
    catch (e) { setError("y₁: " + e.message); return; }
    if (expr2) {
      try { parse(expr2); }
      catch (e) { setError("y₂: " + e.message); return; }
    }
    setError("");
  }

  function fmt(v) {
    if (Math.abs(v) >= 1000 || (Math.abs(v) < 0.01 && v !== 0)) return v.toExponential(1);
    return Math.round(v * 100) / 100;
  }

  function exportPng() {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const link = document.createElement("a");
    link.download = `plot_${Date.now()}.png`;
    link.href = cvs.toDataURL("image/png");
    link.click();
  }

  function shareLink() {
    const p = new URLSearchParams();
    p.set("f1", expr1);
    if (expr2) p.set("f2", expr2);
    Object.entries(params).forEach(([k, v]) => p.set("p_" + k, v));
    p.set("xmin", xMin); p.set("xmax", xMax);
    p.set("ymin", yMin); p.set("ymax", yMax);
    const url = window.location.origin + window.location.pathname + "?" + p.toString();
    navigator.clipboard.writeText(url);
    alert("📋 Link copied. Paste it anywhere to share this exact plot.");
  }

  return (
    <div style={{ background: "#eef1f8", minHeight: "100vh", padding: "20px 16px 40px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{
          background: "linear-gradient(135deg,#1a237e 0%,#3949ab 100%)",
          borderRadius: 14, padding: "20px 24px", marginBottom: 18, color: "#fff",
          boxShadow: "0 4px 18px rgba(26,35,126,0.22)"
        }}>
          <h1 style={{ margin: "0 0 4px", fontSize: "1.5rem", fontWeight: 800 }}>⚛️ Physics Plotter</h1>
          <p style={{ margin: 0, color: "#fff", fontWeight: 500, fontSize: "0.9rem" }}>
            Type any y = f(x). Free parameters become sliders automatically. Works offline · MIT-licensed.
          </p>
        </div>

        {/* Templates */}
        <div style={{
          background: "#fff", border: "1.5px solid #c5cae9",
          borderTop: "5px solid #6a1b9a", borderRadius: 10,
          padding: 16, marginBottom: 14, boxShadow: "0 2px 8px rgba(26,35,126,0.06)"
        }}>
          <div style={{ color: "#4a148c", fontWeight: 800, fontSize: "0.95rem", marginBottom: 10 }}>
            📚 Templates <span style={{ color: "#546e7a", fontWeight: 500, fontSize: "0.78rem" }}>· first-year physics topics</span>
          </div>
          {(() => {
            const groups = [];
            TEMPLATES.forEach((t) => {
              const g = t.group || "Other";
              let row = groups.find(r => r.name === g);
              if (!row) { row = { name: g, items: [] }; groups.push(row); }
              row.items.push(t);
            });
            return groups.map(g => (
              <div key={g.name} style={{ marginBottom: 10 }}>
                <div style={{ color: "#1a237e", fontWeight: 700, fontSize: "0.78rem", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  {g.name}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {g.items.map((t, i) => (
                    <button key={i} onClick={() => applyTemplate(t)} title={t.note || ""} style={{
                      background: "#f5f7ff", color: "#1a237e",
                      border: "1.5px solid #c5cae9", borderRadius: 16,
                      padding: "5px 12px", fontWeight: 600, cursor: "pointer", fontSize: "0.82rem"
                    }}>{t.name}</button>
                  ))}
                </div>
              </div>
            ));
          })()}
        </div>

        {/* Inputs + Sliders | Canvas */}
        <div style={{ display: "grid", gridTemplateColumns: "minmax(280px,360px) 1fr", gap: 14, alignItems: "start" }}>
          {/* Left column: inputs + sliders + ranges */}
          <div style={{
            background: "#fff", border: "1.5px solid #c5cae9",
            borderTop: "5px solid #1565c0", borderRadius: 10,
            padding: 16, boxShadow: "0 2px 8px rgba(26,35,126,0.06)"
          }}>
            <Label>Function 1 — y₁ =</Label>
            <input value={expr1} onChange={e => setExpr1(e.target.value)} style={input} placeholder="e.g. A*sin(omega*x + phi)" />

            <div style={{ height: 10 }} />
            <Label>Function 2 — y₂ = <span style={{ color: "#546e7a", fontWeight: 500 }}>(optional, for comparison)</span></Label>
            <input value={expr2} onChange={e => setExpr2(e.target.value)} style={input} placeholder="(leave empty)" />

            {error && (
              <p style={{ background: "#ffebee", color: "#c62828", borderRadius: 6, padding: "6px 10px", marginTop: 8, fontSize: "0.82rem" }}>⚠ {error}</p>
            )}

            {usedParams.length > 0 && (
              <>
                <div style={{ marginTop: 16, marginBottom: 6, color: "#1a237e", fontWeight: 700, fontSize: "0.88rem", borderBottom: "1px solid #e8eaf6", paddingBottom: 4 }}>
                  🎛 Parameters <span style={{ color: "#546e7a", fontWeight: 500, fontSize: "0.78rem" }}>({usedParams.length})</span>
                </div>
                {usedParams.map(name => (
                  <ParamSlider key={name} name={name}
                    value={params[name] ?? 1}
                    onChange={(v) => setParams(prev => ({ ...prev, [name]: v }))}
                  />
                ))}
              </>
            )}

            <div style={{ marginTop: 16, marginBottom: 6, color: "#1a237e", fontWeight: 700, fontSize: "0.88rem", borderBottom: "1px solid #e8eaf6", paddingBottom: 4 }}>
              🔭 View range
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <NumInput label="x min" value={xMin} onChange={setXMin} />
              <NumInput label="x max" value={xMax} onChange={setXMax} />
              <NumInput label="y min" value={yMin} onChange={setYMin} />
              <NumInput label="y max" value={yMax} onChange={setYMax} />
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
              <button onClick={exportPng} style={btnPrimary}>📥 PNG</button>
              <button onClick={shareLink} style={btnSecondary}>🔗 Copy share link</button>
            </div>
          </div>

          {/* Right column: canvas */}
          <div style={{
            background: "#fff", border: "1.5px solid #c5cae9",
            borderTop: "5px solid #1a237e", borderRadius: 10,
            padding: 14, boxShadow: "0 2px 8px rgba(26,35,126,0.06)",
            minHeight: 540
          }}>
            <canvas ref={canvasRef} style={{ width: "100%", height: 540, display: "block" }} />
          </div>
        </div>

        {/* Practice questions for the current template */}
        {currentTemplate?.questions?.length > 0 && (() => {
          const qs = currentTemplate.questions;
          const q = qs[qIdx];
          const doneSet = completed[currentTemplate.name] || new Set();
          const lvlMeta = {
            "easy":      { bg: "#e8f5e9", color: "#2e7d32", label: "Easy" },
            "medium":    { bg: "#fff3e0", color: "#e65100", label: "Medium" },
            "eamcet":    { bg: "#e0f7fa", color: "#006064", label: "EAMCET style" },
            "jee-mains": { bg: "#fce4ec", color: "#c2185b", label: "JEE-Mains style" },
            "jee-adv":   { bg: "#ede7f6", color: "#4527a0", label: "JEE-Advanced style" },
          };
          const meta = lvlMeta[q.level] || lvlMeta.easy;
          return (
            <div style={{
              background: "#fff", border: "1.5px solid #c5cae9",
              borderTop: "5px solid #c2185b", borderRadius: 10,
              padding: 18, marginTop: 14, boxShadow: "0 2px 8px rgba(26,35,126,0.06)"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                <div>
                  <h3 style={{ margin: 0, color: "#880e4f", fontWeight: 800, fontSize: "1.05rem" }}>
                    📋 Practice Questions <span style={{ color: "#546e7a", fontWeight: 500, fontSize: "0.78rem" }}>· {currentTemplate.name}</span>
                  </h3>
                  <p style={{ margin: "3px 0 0", color: "#546e7a", fontSize: "0.78rem", fontWeight: 500 }}>
                    Original questions in the JEE-Mains style · CC-BY-SA 4.0 © Physics Hub
                  </p>
                </div>
                <div style={{ background: "#f5f7ff", borderRadius: 16, padding: "4px 12px", color: "#1a237e", fontSize: "0.84rem", fontWeight: 700 }}>
                  ✓ {doneSet.size} / {qs.length} solved
                </div>
              </div>

              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                {qs.map((_, i) => (
                  <button key={i} onClick={() => { setQIdx(i); setUserInput(""); setMcqChoice(null); setFeedback(null); setShowHint(false); setShowSolution(false); }}
                    style={{
                      background: i === qIdx ? "#c2185b" : doneSet.has(qs[i].id) ? "#a5d6a7" : "#f5f7ff",
                      color: i === qIdx ? "#fff" : doneSet.has(qs[i].id) ? "#1b5e20" : "#1a237e",
                      border: "1.5px solid " + (i === qIdx ? "#c2185b" : "#c5cae9"),
                      borderRadius: 8, padding: "5px 11px", fontWeight: 700, cursor: "pointer", fontSize: "0.8rem", minWidth: 32
                    }}>{doneSet.has(qs[i].id) ? "✓" : i + 1}</button>
                ))}
              </div>

              <div style={{ background: "#fdf6f9", border: "1px solid #f8bbd0", borderRadius: 10, padding: "14px 16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontWeight: 800, color: "#880e4f", fontSize: "0.95rem" }}>Question {qIdx + 1} of {qs.length}</span>
                  <span style={{ background: meta.bg, color: meta.color, borderRadius: 12, padding: "2px 10px", fontSize: "0.74rem", fontWeight: 700 }}>{meta.label}</span>
                </div>
                <p style={{ margin: "0 0 12px", color: "#1c2833", fontSize: "0.94rem", lineHeight: 1.6 }}>{q.prompt}</p>

                {q.type === "numeric" && (
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
                    <input
                      type="number"
                      value={userInput}
                      onChange={e => { setUserInput(e.target.value); setFeedback(null); }}
                      placeholder="Type your numeric answer"
                      style={{ padding: "8px 10px", borderRadius: 7, border: "1.5px solid #c5cae9", fontSize: "0.95rem", minWidth: 200, fontFamily: "ui-monospace, monospace" }}
                      onKeyDown={e => e.key === "Enter" && checkAnswer()}
                    />
                    {q.unit && <span style={{ color: "#37474f", fontWeight: 600 }}>{q.unit}</span>}
                    <button onClick={checkAnswer} style={{ background: "#c2185b", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontWeight: 700, cursor: "pointer", fontSize: "0.88rem" }}>✓ Check</button>
                  </div>
                )}

                {q.type === "mcq" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
                    {q.options.map((opt, i) => (
                      <label key={i} style={{
                        display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
                        background: mcqChoice === i ? "#fce4ec" : "#fff",
                        border: "1.5px solid " + (mcqChoice === i ? "#c2185b" : "#e8eaf6"),
                        borderRadius: 8, cursor: "pointer"
                      }}>
                        <input type="radio" name="mcq" checked={mcqChoice === i} onChange={() => { setMcqChoice(i); setFeedback(null); }} />
                        <span style={{ color: "#1c2833", fontWeight: 500 }}>{String.fromCharCode(65 + i)}. {opt}</span>
                      </label>
                    ))}
                    <button onClick={checkAnswer} style={{ alignSelf: "flex-start", marginTop: 4, background: "#c2185b", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontWeight: 700, cursor: "pointer", fontSize: "0.88rem" }}>✓ Check</button>
                  </div>
                )}

                {feedback === "correct" && (
                  <div style={{ background: "#e8f5e9", color: "#1b5e20", border: "1.5px solid #a5d6a7", borderRadius: 8, padding: "8px 12px", marginBottom: 8, fontWeight: 700 }}>
                    ✅ Correct! Well done.
                  </div>
                )}
                {feedback === "wrong" && (
                  <div style={{ background: "#ffebee", color: "#c62828", border: "1.5px solid #ef9a9a", borderRadius: 8, padding: "8px 12px", marginBottom: 8, fontWeight: 600 }}>
                    ❌ Not quite. Try again, use the plot, or peek at the hint below.
                  </div>
                )}

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                  {q.hint && (
                    <button onClick={() => setShowHint(s => !s)} style={{ background: "#fff", color: "#1565c0", border: "1.5px solid #90caf9", borderRadius: 7, padding: "5px 12px", fontWeight: 700, cursor: "pointer", fontSize: "0.8rem" }}>
                      {showHint ? "🔼 Hide hint" : "💡 Show hint"}
                    </button>
                  )}
                  <button onClick={() => setShowSolution(s => !s)} style={{ background: "#fff", color: "#6a1b9a", border: "1.5px solid #ce93d8", borderRadius: 7, padding: "5px 12px", fontWeight: 700, cursor: "pointer", fontSize: "0.8rem" }}>
                    {showSolution ? "🔼 Hide solution" : "📖 Show solution"}
                  </button>
                </div>

                {showHint && q.hint && (
                  <div style={{ background: "#e3f2fd", color: "#0d47a1", border: "1px solid #90caf9", borderRadius: 7, padding: "8px 12px", marginBottom: 6, fontSize: "0.86rem" }}>
                    💡 <strong>Hint:</strong> {q.hint}
                  </div>
                )}
                {showSolution && q.solution && (
                  <div style={{ background: "#f3e5f5", color: "#4a148c", border: "1px solid #ce93d8", borderRadius: 7, padding: "8px 12px", fontSize: "0.86rem" }}>
                    📖 <strong>Solution:</strong> {q.solution}
                  </div>
                )}
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12 }}>
                <button onClick={() => nextQuestion(-1)} disabled={qIdx === 0} style={{ background: "#fff", color: "#1a237e", border: "1.5px solid #c5cae9", borderRadius: 8, padding: "7px 14px", fontWeight: 700, cursor: qIdx === 0 ? "default" : "pointer", opacity: qIdx === 0 ? 0.5 : 1, fontSize: "0.84rem" }}>← Previous</button>
                <button onClick={() => nextQuestion(+1)} disabled={qIdx === qs.length - 1} style={{ background: "#1a237e", color: "#fff", border: "none", borderRadius: 8, padding: "7px 14px", fontWeight: 700, cursor: qIdx === qs.length - 1 ? "default" : "pointer", opacity: qIdx === qs.length - 1 ? 0.5 : 1, fontSize: "0.84rem" }}>Next →</button>
              </div>
            </div>
          );
        })()}

        {currentTemplate && !currentTemplate.questions && (
          <div style={{ marginTop: 14, padding: "10px 14px", background: "#fff8e1", borderRadius: 8, fontSize: "0.84rem", color: "#bf360c", fontWeight: 500, border: "1px solid #ffe082" }}>
            📋 Practice questions for <strong>{currentTemplate.name}</strong> are coming soon. The other templates already have JEE-Mains-style problems — try Free Fall, Projectile, SHM, or Pendulum.
          </div>
        )}

        <div style={{ marginTop: 14, padding: "8px 14px", background: "#f5f7ff", borderRadius: 8, fontSize: "0.82rem", color: "#37474f", fontWeight: 500 }}>
          💡 Supported syntax: <code>+ − * / ^</code> · functions <code>sin cos tan exp log sqrt abs</code> · constants <code>pi e</code> · use any other letter (a, b, A, omega, phi…) and it becomes a slider.
        </div>
      </div>
    </div>
  );
}

/* ── tiny UI helpers ── */
function ParamSlider({ name, value, onChange }) {
  // Heuristic range based on current magnitude
  const mag = Math.max(Math.abs(value), 0.0001);
  const span = mag * 4;
  const min = value - span;
  const max = value + span;
  const step = mag / 100;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontWeight: 700, color: "#1a237e", fontSize: "0.86rem" }}>{name}</span>
        <input
          type="number"
          value={Number.isFinite(value) ? value : ""}
          onChange={e => onChange(parseFloat(e.target.value))}
          style={{ width: 90, padding: "3px 6px", fontSize: "0.82rem", borderRadius: 5, border: "1px solid #c5cae9", textAlign: "right" }}
        />
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        style={{ width: "100%", accentColor: "#1a237e" }} />
    </div>
  );
}
function Label({ children }) {
  return <label style={{ display: "block", color: "#1a237e", fontWeight: 700, fontSize: "0.84rem", marginBottom: 4 }}>{children}</label>;
}
const input = { width: "100%", padding: "8px 10px", borderRadius: 7, border: "1.5px solid #c5cae9", fontSize: "0.9rem", fontFamily: "ui-monospace, monospace" };
const btnPrimary = { background: "#1a237e", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontWeight: 700, cursor: "pointer", fontSize: "0.85rem" };
const btnSecondary = { background: "#fff", color: "#1a237e", border: "1.5px solid #c5cae9", borderRadius: 8, padding: "8px 14px", fontWeight: 700, cursor: "pointer", fontSize: "0.85rem" };
function NumInput({ label, value, onChange }) {
  return (
    <div>
      <div style={{ color: "#37474f", fontWeight: 600, fontSize: "0.78rem", marginBottom: 2 }}>{label}</div>
      <input type="number" value={value} onChange={e => onChange(parseFloat(e.target.value))}
        style={{ ...input, fontFamily: "inherit", padding: "6px 8px", fontSize: "0.85rem" }} />
    </div>
  );
}
